import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  DestroyRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, combineLatest, map } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LucideAngularModule, X, Rocket, Check, Star, Loader2 } from 'lucide-angular';
import { BillingApiService, Product } from '../billing-api.service';
import { CheckoutModalComponent } from '../checkout-modal/checkout-modal';
import { AmplitudeService } from '../../../common/analytics/amplitude.service';

export type PaywallTarget =
  | 'dashboard_subscription'
  | 'export_pdf'
  | 'export_pptx'
  | 'export_docx'
  | 'export_google_slides'
  | 'export_google_docs'
  | 'grading_limit';

@Component({
  selector: 'app-pricing-modal',
  standalone: true,
  imports: [CommonModule, LucideAngularModule, CheckoutModalComponent],
  templateUrl: './pricing-modal.html',
  styleUrl: './pricing-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PricingModalComponent implements OnInit, OnChanges {
  private readonly billingService = inject(BillingApiService);
  private readonly amplitudeService = inject(AmplitudeService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly isOpen$$ = new BehaviorSubject<boolean>(false);
  protected readonly isOpen$ = this.isOpen$$.asObservable();

  private readonly products$$ = new BehaviorSubject<Product[]>([]);
  protected readonly products$ = this.products$$.asObservable();

  private readonly isLoading$$ = new BehaviorSubject<boolean>(false);
  protected readonly isLoading$ = this.isLoading$$.asObservable();

  private readonly selectedProductId$$ = new BehaviorSubject<string | null>(null);
  protected readonly selectedProductId$ = this.selectedProductId$$.asObservable();

  /** Selected product observable for template use */
  protected readonly selectedProduct$ = combineLatest([
    this.products$$,
    this.selectedProductId$$,
  ]).pipe(map(([products, id]) => products.find((p) => p.id === id) || null));

  /** Whether to show checkout modal instead of pricing */
  private readonly showCheckout$$ = new BehaviorSubject<boolean>(false);
  protected readonly showCheckout$ = this.showCheckout$$.asObservable();

  /** Selected product for checkout */
  private readonly checkoutProduct$$ = new BehaviorSubject<Product | null>(null);
  protected readonly checkoutProduct$ = this.checkoutProduct$$.asObservable();

  @Input()
  set isOpen(value: boolean) {
    this.isOpen$$.next(value);
    if (value && this.products$$.value.length === 0) {
      this.loadProducts();
    }
  }

  /** The target action that triggered opening the paywall */
  @Input() target: PaywallTarget = 'dashboard_subscription';

  @Output() readonly closed = new EventEmitter<void>();
  @Output() readonly productSelected = new EventEmitter<Product>();
  @Output() readonly paymentSuccess = new EventEmitter<Product>();

  protected readonly icons = {
    X,
    Rocket,
    Check,
    Star,
    Loader2,
  };

  ngOnInit(): void {
    // Pre-load products
    this.loadProducts();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Track paywall displayed when modal opens
    if (changes['isOpen'] && this.isOpen$$.value) {
      this.amplitudeService.trackEvent('paywall_displayed', { target: this.target });
    }
  }

  private loadProducts(): void {
    this.isLoading$$.next(true);
    this.billingService
      .getProducts()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (products) => {
          this.products$$.next(products);
          // Select default product or first one
          const defaultProduct = products.find((p) => p.metadata.isDefault) || products[0];
          if (defaultProduct && !this.selectedProductId$$.value) {
            this.selectedProductId$$.next(defaultProduct.id);
          }
          this.isLoading$$.next(false);
        },
        error: () => {
          this.isLoading$$.next(false);
        },
      });
  }

  protected close(): void {
    this.closed.emit();
  }

  protected selectProduct(product: Product): void {
    this.selectedProductId$$.next(product.id);
    this.amplitudeService.trackEvent('plan_selected', this.getProductEventProps(product));
  }

  protected onGetStarted(): void {
    const selectedId = this.selectedProductId$$.value;
    const products = this.products$$.value;
    const selectedProduct = products.find((p) => p.id === selectedId);
    if (selectedProduct) {
      this.amplitudeService.trackEvent('get_started_clicked', this.getProductEventProps(selectedProduct));
      this.productSelected.emit(selectedProduct);
      // Open checkout modal with selected product
      this.checkoutProduct$$.next(selectedProduct);
      this.showCheckout$$.next(true);
    }
  }

  private getProductEventProps(product: Product): Record<string, unknown> {
    return {
      productId: product.id,
      price: product.price.amount,
      interval: product.price.interval,
      hasTrial: product.hasTrial,
    };
  }

  protected getBadgeClasses(badgeStyle: string | null): string {
    switch (badgeStyle) {
      case 'teal':
        return 'bg-brand text-white';
      case 'yellow':
        return 'bg-amber-400 text-gray-900';
      default:
        return 'bg-gray-200 text-gray-700';
    }
  }

  /** Handle checkout modal close */
  protected onCheckoutClosed(): void {
    this.showCheckout$$.next(false);
    this.checkoutProduct$$.next(null);
  }

  /** Handle going back from checkout to pricing */
  protected onCheckoutGoBack(): void {
    this.showCheckout$$.next(false);
    this.checkoutProduct$$.next(null);
  }

  /** Handle payment success */
  protected onPaymentSuccess(product: Product): void {
    this.paymentSuccess.emit(product);
    this.showCheckout$$.next(false);
    this.checkoutProduct$$.next(null);
    this.close();
  }

  /** Get trial period text based on product */
  protected getTrialPeriodText(product: Product): string {
    if (!product.hasTrial || !product.price.trialPeriod) {
      return '';
    }
    // trialPeriod is stored in minutes, convert to days
    const days = Math.round(product.price.trialPeriod / 60 / 24);
    if (days === 7) {
      return '7-day';
    } else if (days === 30 || days === 31) {
      return 'monthly';
    }
    return `${days}-day`;
  }

  /** Get billing interval text */
  protected getBillingIntervalText(product: Product): string {
    if (product.price.interval === 'year') {
      return 'per year';
    }
    return 'per month';
  }

  /** Format price for display */
  protected formatPrice(amount: number): string {
    return `$${(amount / 100).toFixed(2)}`;
  }

  /** Format price with currency code for display (e.g., "USD 39.99") */
  protected formatPriceWithCurrency(amount: number, currency: string): string {
    return `${currency.toUpperCase()} ${(amount / 100).toFixed(2)}`;
  }
}

